import { useEffect, useState } from "react";

import { parseDetail } from "../utils/errors";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import { useTenant } from "../hooks/useTenant";
import useRequests from "../hooks/http";

export default function Applications() {
  const { t } = useTranslation();
  const { get, post } = useRequests();
  const { activeTenant, loading: tenantLoading } = useTenant();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newApp, setNewApp] = useState({ name: "", identifier: "", description: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!activeTenant) {
      setApplications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    get(`/admin/tenants/${activeTenant.id}/applications`)
      .then(({ data, status }) => {
        if (status === 200 && Array.isArray(data)) {
          setApplications(data);
        } else if (status !== 200) {
          setError(parseDetail(data?.detail, "Failed to load applications"));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [activeTenant, get]);

  const handleCreate = async () => {
    if (!activeTenant) return;
    setError("");
    const { data, status } = await post(`/admin/tenants/${activeTenant.id}/applications`, null, newApp);
    if (status === 201 || status === 200) {
      setApplications((prev) => [...prev, data]);
      setCreateOpen(false);
      setNewApp({ name: "", identifier: "", description: "" });
    } else {
      setError(parseDetail(data?.detail, "Failed to create application"));
    }
  };

  if (tenantLoading || loading) return <LinearProgress />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h4">{t("applications.title")}</Typography>
        {activeTenant && (
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            {t("applications.newButton")}
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("applications.description")}
      </Typography>

      {error && !createOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {applications.length === 0 && !error && activeTenant && (
        <Typography color="text.secondary">No applications found.</Typography>
      )}

      <Grid container spacing={2}>
        {applications.map((app) => (
          <Grid item xs={12} sm={6} md={4} key={app.id}>
            <Card variant="outlined">
              <CardActionArea onClick={() => navigate(`/app/applications/${app.id}/clients`)}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <AppsIcon color="primary" />
                    <Typography variant="h6">{app.name}</Typography>
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: app.active ? "success.main" : "text.disabled",
                        flexShrink: 0,
                        ml: "auto",
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {app.identifier}
                  </Typography>
                  {app.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {app.description}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("applications.description")}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newApp.name}
            onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
          />
          <TextField
            label="Identifier"
            fullWidth
            margin="normal"
            placeholder="https://api.myapp.com"
            helperText={t("applications.identifierHelp")}
            value={newApp.identifier}
            onChange={(e) => setNewApp({ ...newApp, identifier: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={newApp.description}
            onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newApp.name || !newApp.identifier}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
